import { maxLength, required } from '@reliance/forms-js/lib/validation';
import { TvSource } from '@reliance/types/models/Tv';
import { FORM_ERROR } from 'final-form';
import React, { useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import ErrorMessage from '../common/ErrorMessage';
import api from '../common/api';
import StateGroupedSelect from '../facility/StateGroupedSelect';
import { TextInput } from '../form/TextInput';
import { useFacilities } from '../hooks/Facility';
import { useHousingUnits } from '../hooks/HousingUnit';
import ConfigurationFields from './ConfigurationFields';
import DeviceInfoPanel from './DeviceInfoPanel';
import TelemetryPanel from './TelemetryPanel';

type AddEditModalProps = {
  source: TvSource | null;
  onClose(saved: boolean): void;
};

const AddEditModal: React.FC<AddEditModalProps> = (props) => {
  const source = props.source;
  const facilities = useFacilities();
  const facilityList = facilities.result ?? [];
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(
    source?.facility?.id != null ? String(source.facility.id) : null
  );
  const housingUnitFilters = useMemo(
    () => (selectedFacilityId ? { facilityId: selectedFacilityId } : undefined),
    [selectedFacilityId]
  );
  const housingUnits = useHousingUnits(housingUnitFilters);
  const housingUnitList = housingUnits.result?.data ?? [];

  const handleFormSubmit = async (data: Record<string, any>) => {
    try {
      const facilityId = data.facilityId === '' || data.facilityId == null ? null : Number(data.facilityId);
      const payload: Record<string, unknown> = {
        name: data.name ?? null,
        enabled: data.enabled === true || data.enabled === 'true',
        facilityId: facilityId ?? undefined,
        streamUrl: data.streamUrl ?? null,
        localIpAddress: data.localIpAddress ?? null,
        relayIngestUrl: data.relayIngestUrl ?? '',
        relayControlUrl: data.relayControlUrl ?? '',
        housingUnitIds: Array.isArray(data.housingUnitIds) ? data.housingUnitIds : [],
      };

      if (source) {
        payload.configuration = data.configuration;
        await api.put(`/tv/source/${source.id}`, payload);
      } else {
        await api.post('/tv/source', { macAddress: data.macAddress, ...payload });
      }
      props.onClose(true);
    } catch (error: any) {
      const msg = error.response?.data?.message ?? error.response?.data?.error ?? 'Failed to save TV stream';
      const details = error.response?.data?.details ?? error.response?.data?.errors;
      const display = details && typeof details === 'object'
        ? `${msg}: ${Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')}`
        : msg;
      return { [FORM_ERROR]: display };
    }
  };

  const initialValues = useMemo(() => {
    const configuration = source?.configuration;
    return {
      name: source?.name ?? null,
      macAddress: source?.macAddress ?? null,
      enabled: source?.enabled ?? false,
      facilityId: source?.facility?.id != null ? String(source.facility.id) : '',
      streamUrl: source?.streamUrl ?? null,
      localIpAddress: source?.localIpAddress ?? null,
      relayIngestUrl: source?.relayIngestUrl ?? null,
      relayControlUrl: source?.relayControlUrl ?? null,
      housingUnitIds: (source?.housingUnits ?? [])
        .map((hu) => hu.id)
        .filter((id): id is number => id != null),
      configuration: {
        audio: {
          device: configuration?.audio?.device,
          bufferSize: configuration?.audio?.bufferSize,
          captureGainPercent: configuration?.audio?.captureGainPercent,
          captureControl: configuration?.audio?.captureControl,
          disableAgc: configuration?.audio?.disableAgc == null ? null : String(configuration.audio.disableAgc),
          autoGain: configuration?.audio?.autoGain == null ? null : String(configuration.audio.autoGain),
          autoGainTargetDbfs: configuration?.audio?.autoGainTargetDbfs,
          ringBufferMultiplier: configuration?.audio?.ringBufferMultiplier,
        },
        network: {
          port: configuration?.network?.port,
        },
      },
    };
  }, [source]);

  return (
    <Modal show={true} onHide={() => props.onClose(false)} size="xl">
      <FinalForm
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        render={({ handleSubmit, submitting, submitError, values }) => {
          const facilityIdForHousing = values.facilityId === '' ? undefined : values.facilityId;

          return (
            <>
              <FormSpy
                subscription={{ values: true }}
                onChange={(state) => {
                  const fid = state.values?.facilityId;
                  setSelectedFacilityId(fid === '' || fid == null ? null : String(fid));
                }}
              />
              <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                  <Modal.Title>{source ? 'Edit TV Stream' : 'Add TV Stream'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {submitError && (
                    <ErrorMessage
                      error={submitError as any}
                      httpGenericMessage="An error occurred while saving the TV stream"
                    />
                  )}

                  <Row>
                    <Col lg={6}>
                      <Row className="mb-3">
                        <Col lg={12}>
                          <Field name="enabled" type="checkbox">
                            {({ input }) => {
                              const enabled = !!input.checked;
                              return (
                                <Form.Group controlId="tv-source-enabled">
                                  <div className="d-flex align-items-center gap-2">
                                    <span>Status:</span>
                                    <Form.Check type="switch" className="mb-0">
                                      <Form.Check.Input
                                        id="tv-source-enabled"
                                        type="checkbox"
                                        role="switch"
                                        checked={enabled}
                                        onChange={input.onChange}
                                        onBlur={input.onBlur}
                                        name={input.name}
                                        style={
                                          !enabled
                                            ? {
                                                backgroundColor: 'var(--bs-danger)',
                                                borderColor: 'var(--bs-danger)',
                                              }
                                            : undefined
                                        }
                                      />
                                    </Form.Check>
                                    <span>{enabled ? 'Enabled' : 'Disabled'}</span>
                                  </div>
                                </Form.Group>
                              );
                            }}
                          </Field>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg={12}>
                          <TextInput
                            label="Name"
                            name="name"
                            validationRules={[maxLength(255)]}
                          />
                        </Col>
                      </Row>

                      <Row className="mt-2">
                        <Col lg={12}>
                          <StateGroupedSelect
                            label="Facility"
                            name="facilityId"
                            facilities={facilityList}
                            emptyLabel="None"
                          />
                        </Col>
                      </Row>

                      {source ? (
                        <>
                          <DeviceInfoPanel
                            macAddress={source.macAddress}
                            localIpAddress={source.localIpAddress}
                            streamUrl={source.streamUrl}
                            relayIngestUrl={source.relayIngestUrl}
                            relayControlUrl={source.relayControlUrl}
                          />
                          <ConfigurationFields
                            configVersion={source.configVersion}
                            appliedConfigVersion={source.appliedConfigVersion}
                          />
                        </>
                      ) : (
                        <>
                          <Row className="mt-2">
                            <Col lg={12}>
                              <TextInput
                                label="MAC Address"
                                name="macAddress"
                                validationRules={[required, maxLength(255)]}
                              />
                            </Col>
                          </Row>
                          <Row className="mt-2">
                            <Col lg={12}>
                              <TextInput
                                label="Local IP Address"
                                name="localIpAddress"
                                validationRules={[maxLength(45)]}
                                disabled
                              />
                            </Col>
                          </Row>
                        </>
                      )}
                    </Col>

                    <Col lg={6}>
                      {facilityIdForHousing ? (
                        <>
                          <Form.Label className="d-block">Housing Units (optional)</Form.Label>
                          {housingUnits.loading ? (
                            <span className="text-muted">Loading housing units...</span>
                          ) : housingUnitList.length === 0 ? (
                            <span className="text-muted">
                              No housing units configured for this facility.
                            </span>
                          ) : (
                            <Field name="housingUnitIds">
                              {({ input }) => {
                                const value = (input.value ?? []) as number[];
                                return (
                                  <div className="d-flex flex-column" style={{ maxHeight: 320, overflowY: 'auto' }}>
                                    {housingUnitList.map((hu) => {
                                      const checked = value.includes(hu.id);
                                      return (
                                        <Form.Check
                                          key={hu.id}
                                          id={`housing-unit-${hu.id}`}
                                          type="checkbox"
                                          label={hu.name ?? `Unit ${hu.id}`}
                                          checked={checked}
                                          onChange={() => {
                                            const next = checked
                                              ? value.filter((id) => id !== hu.id)
                                              : [...value, hu.id];
                                            input.onChange(next);
                                          }}
                                        />
                                      );
                                    })}
                                  </div>
                                );
                              }}
                            </Field>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">Select a facility to choose housing units.</span>
                      )}
                    </Col>
                  </Row>

                  {source != null && <TelemetryPanel source={source} />}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => props.onClose(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save'}
                  </Button>
                </Modal.Footer>
              </form>
            </>
          );
        }}
      />
    </Modal>
  );
};

export default AddEditModal;
